import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { countries, Country, getSortedCountries } from '@/data/countries'

// Debounce hook for search
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

interface CountrySelectorProps {
  value?: Country
  onChange: (country: Country) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  error?: string
}

export function CountrySelector({
  value,
  onChange,
  placeholder = "Selecionar país",
  className,
  disabled = false,
  error
}: CountrySelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  // Debounce search term to improve performance
  const debouncedSearchTerm = useDebounce(searchTerm, 150)

  // Memoize sorted countries to avoid recalculation
  const sortedCountries = useMemo(() => getSortedCountries(), [])

  // Memoize filtered countries based on debounced search term
  const filteredCountries = useMemo(() => {
    if (debouncedSearchTerm.trim() === '') {
      return sortedCountries
    }
    
    const lowerSearchTerm = debouncedSearchTerm.toLowerCase()
    return sortedCountries.filter(country =>
      country.name.toLowerCase().includes(lowerSearchTerm) ||
      country.phoneCode.includes(debouncedSearchTerm) ||
      country.nationality.toLowerCase().includes(lowerSearchTerm)
    )
  }, [debouncedSearchTerm, sortedCountries])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus()
    }
  }, [isOpen])

  const handleCountrySelect = useCallback((country: Country) => {
    onChange(country)
    setIsOpen(false)
    setSearchTerm('')
  }, [onChange])

  const handleToggle = useCallback(() => {
    if (!disabled) {
      setIsOpen(!isOpen)
      if (!isOpen) {
        setSearchTerm('')
      }
    }
  }, [disabled, isOpen])

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={cn(
          "w-full flex items-center justify-between px-3 py-2 text-left border rounded-lg transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-20",
          error 
            ? "border-error focus:border-error focus:ring-error/20" 
            : "border-gray-300 focus:border-primary",
          disabled 
            ? "bg-gray-50 text-gray-400 cursor-not-allowed" 
            : "bg-white text-gray-900 hover:border-gray-400"
        )}
      >
        <div className="flex items-center space-x-2">
          {value ? (
            <>
              <span className="text-lg">{value.flag}</span>
              <span className="font-medium">{value.name}</span>
              <span className="text-gray-500">({value.phoneCode})</span>
            </>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        <ChevronDown 
          className={cn(
            "h-4 w-4 text-gray-400 transition-transform",
            isOpen && "rotate-180"
          )} 
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Pesquisar país..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-20 focus:border-primary"
              />
            </div>
          </div>

          {/* Countries List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleCountrySelect(country)}
                  className={cn(
                    "w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors",
                    value?.code === country.code && "bg-primary/10 text-primary"
                  )}
                >
                  <span className="text-lg">{country.flag}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900">{country.name}</div>
                    <div className="text-sm text-gray-500">
                      {country.phoneCode} • {country.nationality}
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="px-3 py-4 text-center text-gray-500">
                Nenhum país encontrado
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-error">{error}</p>
      )}
    </div>
  )
}
